import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import AppLayout from './components/layout/AppLayout';
import useAuthStore from './store/authStore';
import useLang from './i18n';
import { LoadingSpinner } from './components/common';

const Dashboard       = lazy(() => import('./pages/Dashboard'));
const Journals        = lazy(() => import('./pages/Journals'));
const Customers       = lazy(() => import('./pages/Customers'));
const CustomerProfile = lazy(() => import('./pages/CustomerProfile'));
const Invoices        = lazy(() => import('./pages/Invoices'));
const Expenses        = lazy(() => import('./pages/Expenses'));
const Revenues        = lazy(() => import('./pages/Revenues'));
const Accounts        = lazy(() => import('./pages/Accounts'));
const Cashbox         = lazy(() => import('./pages/Cashbox'));
const Bank            = lazy(() => import('./pages/Bank'));
const Budget          = lazy(() => import('./pages/Budget'));
const IncomeStatement = lazy(() => import('./pages/IncomeStatement'));
const Products        = lazy(() => import('./pages/Products'));
const ProductDetail   = lazy(() => import('./pages/ProductDetail'));
const Sales           = lazy(() => import('./pages/Sales'));
const SaleDetail      = lazy(() => import('./pages/SaleDetail'));
const NewSale         = lazy(() => import('./pages/NewSale'));
const Purchases       = lazy(() => import('./pages/Purchases'));
const PurchaseDetail  = lazy(() => import('./pages/PurchaseDetail'));
const Suppliers       = lazy(() => import('./pages/Suppliers'));
const SupplierProfile = lazy(() => import('./pages/SupplierProfile'));
const Partners        = lazy(() => import('./pages/Partners'));
const Inventory       = lazy(() => import('./pages/Inventory'));
const Assets          = lazy(() => import('./pages/Assets'));
const FiscalYears     = lazy(() => import('./pages/FiscalYears'));

const { TrialBalance, Login } = require('./pages/Reports');
const ReportsPage = require('./pages/ReportsPage').default;
const { Users, Branches }      = require('./pages/UsersAndBranches');
const { AuditTrail, Settings } = require('./pages/AuditAndSettings');

const PH = ({ t }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center text-3xl mb-4">🏗️</div>
    <h2 className="text-lg font-bold text-gray-600">{t}</h2>
    <p className="text-sm text-gray-400 mt-1">{t('loading')}</p>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30000 } },
});

const Guard = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

export default function App() {
  const { lang } = useLang();
  // Set document direction based on language
  React.useEffect(() => {
    const isEn = lang === 'en';
    document.documentElement.dir       = isEn ? 'ltr' : 'rtl';
    document.documentElement.lang      = isEn ? 'en' : 'fa';
    document.documentElement.setAttribute('dir', isEn ? 'ltr' : 'rtl');
    document.body.dir                  = isEn ? 'ltr' : 'rtl';
    document.body.style.direction      = isEn ? 'ltr' : 'rtl';
    document.body.style.textAlign      = isEn ? 'left' : 'right';
    document.body.style.fontFamily     = isEn
      ? "'Segoe UI', Arial, sans-serif"
      : "'Vazirmatn', 'Segoe UI', sans-serif";
  }, [lang]);
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><LoadingSpinner /></div>}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Guard><AppLayout /></Guard>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard"             element={<Dashboard />} />
              {/* فروش */}
              <Route path="sales"                 element={<Sales />} />
              <Route path="sales/new"             element={<NewSale />} />
              <Route path="sales/:id"             element={<SaleDetail />} />
              <Route path="invoices"              element={<Invoices />} />
              <Route path="invoices/:id"          element={<SaleDetail />} />
              <Route path="customers"             element={<Customers />} />
              <Route path="customers/:id/profile" element={<CustomerProfile />} />
              {/* خریدها */}
              <Route path="purchases"             element={<Purchases />} />
              <Route path="purchases/:id"         element={<PurchaseDetail />} />
              <Route path="suppliers"             element={<Suppliers />} />
              <Route path="suppliers/:id"         element={<SupplierProfile />} />
              {/* موجودی */}
              <Route path="products"              element={<Products />} />
              <Route path="products/:id"          element={<ProductDetail />} />
              <Route path="inventory"             element={<Inventory />} />
              {/* حسابداری */}
              <Route path="journals"              element={<Journals />} />
              <Route path="ledger"                element={<PH t="دفتر کل" />} />
              <Route path="trial-balance"         element={<TrialBalance />} />
              <Route path="income"                element={<IncomeStatement />} />
              <Route path="balance-sheet"         element={<PH t="ترازنامه" />} />
              <Route path="cash-flow"             element={<PH t="جریان نقدی" />} />
              <Route path="accounts"              element={<Accounts />} />
              {/* امور مالی */}
              <Route path="cashbox"               element={<Cashbox />} />
              <Route path="bank"                  element={<Bank />} />
              <Route path="expenses"              element={<Expenses />} />
              <Route path="revenues"              element={<Revenues />} />
              <Route path="partners"              element={<Partners />} />
              {/* مدیریت */}
              <Route path="budget"                element={<Budget />} />
              <Route path="assets"                element={<Assets />} />
              <Route path="fiscal-years"          element={<FiscalYears />} />
              <Route path="reports"               element={<ReportsPage />} />
              <Route path="branches"              element={<Branches />} />
              <Route path="users"                 element={<Users />} />
              <Route path="audit"                 element={<AuditTrail />} />
              <Route path="settings"              element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
